import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  SIM_API_KEY_INPUT,
  SIM_DEMO_API_KEY,
  SIM_SENSOR_PROVIDER_KEY,
} from '@/common/utils/simulatorIds';
import { Form, message } from 'antd';
import {
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { useNavigate } from 'react-router-dom';
import {
  LinkSensorsAccountBulk,
  VerifyCompanySensor,
} from '@/apis/onboarding.js';
import { useSpin } from '@/components/SpinContext.jsx';
import useToken from '@/common/helper/useToken';
import type {
  ApiError,
  CredentialFormValues,
  CredentialSet,
  LinkSensorPayload,
  SensorProviderDetailsController,
  SensorProviderDetailsProps,
} from '../types';

let credentialKeyCounter = 0;

export const buildBlankCredentialValues = (
  requiredInputs: string[],
  providerKey?: string | null
): Record<string, string> =>
  requiredInputs.reduce(
    (acc, inputName) => ({
      ...acc,
      // ZoomLogi Simulator: prefill the public demo key; Verify still checks it.
      [inputName]:
        providerKey === SIM_SENSOR_PROVIDER_KEY &&
        inputName === SIM_API_KEY_INPUT
          ? SIM_DEMO_API_KEY
          : '',
    }),
    { name: '', description: '' }
  );

const getSensorDescriptionValue = (
  providerData: SensorProviderDetailsProps['providerData']
): string => {
  const sensorDescription =
    typeof providerData?.sensor_description === 'string'
      ? providerData.sensor_description.trim()
      : '';

  if (sensorDescription) return sensorDescription;

  return typeof providerData?.description === 'string'
    ? providerData.description.trim()
    : '';
};

const getCredentialLocalKey = (id?: string): string => {
  if (id) return `credential-${id}`;
  credentialKeyCounter += 1;
  return `credential-new-${credentialKeyCounter}`;
};

const syncPrimaryByOrder = (items: CredentialSet[]): CredentialSet[] =>
  items.map((item, index) => ({
    ...item,
    is_primary: index === 0,
    order_index: index,
  }));

const markOrderUpdated = (items: CredentialSet[]): CredentialSet[] =>
  syncPrimaryByOrder(items).map((item) => ({
    ...item,
    is_update: item.id ? true : item.is_update,
  }));

const buildDefaultCredentialName = (
  providerName: string | undefined,
  index: number
): string =>
  `${providerName || 'Sensor'} credentials${index > 0 ? ` ${index + 1}` : ''}`;

const normalizeCredentialSet = (
  item: NonNullable<
    SensorProviderDetailsProps['providerData']['credentials']
  >[number],
  providerName: string | undefined,
  index: number
): CredentialSet => {
  const credential = item?.credential || item?.credentials || {};
  return {
    id: item?.id,
    local_key: getCredentialLocalKey(item?.id),
    name:
      item?.credential_name ||
      item?.name ||
      buildDefaultCredentialName(providerName, index),
    description: item?.description || '',
    is_primary: Boolean(item?.is_primary),
    order_index: item?.order_index ?? index,
    is_update: Boolean(item?.is_update),
    is_delete: Boolean(item?.is_delete),
    credential,
  };
};

const getInitialCredentialSets = (
  providerData: SensorProviderDetailsProps['providerData'],
  credentials: SensorProviderDetailsProps['credentials']
): CredentialSet[] => {
  const providerName = providerData?.sensor_name || providerData?.name;
  const apiCredentials = Array.isArray(providerData?.credentials)
    ? providerData.credentials
    : [];
  const normalized = apiCredentials.map((item, index) =>
    normalizeCredentialSet(item, providerName, index)
  );

  const pendingCredential = credentials?.credentials;
  if (pendingCredential && Object.keys(pendingCredential).length > 0) {
    normalized.push({
      local_key: getCredentialLocalKey(),
      name: buildDefaultCredentialName(providerName, normalized.length),
      description: '',
      is_primary: normalized.length === 0,
      is_update: false,
      is_delete: false,
      credential: pendingCredential,
    });
  }

  if (
    normalized.length === 0 &&
    providerData?.credential &&
    Object.keys(providerData.credential).length > 0
  ) {
    normalized.push({
      local_key: getCredentialLocalKey(),
      name: buildDefaultCredentialName(providerName, 0),
      description: '',
      is_primary: true,
      is_update: false,
      is_delete: false,
      credential: providerData.credential,
    });
  }

  const primaryIndex = normalized.findIndex((cred) => cred.is_primary);
  const ordered =
    primaryIndex > 0
      ? [
          normalized[primaryIndex],
          ...normalized.filter((_, index) => index !== primaryIndex),
        ]
      : normalized;

  return syncPrimaryByOrder(ordered);
};

export const useSensorProviderDetails = ({
  credentials,
  drawerCredentialView,
  onClose,
  providerData,
  setDrawerCredentialView,
  setModalState,
}: Pick<
  SensorProviderDetailsProps,
  | 'credentials'
  | 'drawerCredentialView'
  | 'onClose'
  | 'providerData'
  | 'setDrawerCredentialView'
  | 'setModalState'
>): SensorProviderDetailsController => {
  const [form] = Form.useForm();
  const [credentialForm] = Form.useForm();
  const navigate = useNavigate();
  const { showSpin, hideSpin } = useSpin();
  const { token: storedToken, removeToken } = useToken();
  // API helpers take a string; an expired session is handled by unauthorize.
  const token = storedToken ?? '';
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [notification] = useState(false);
  const [, setIsFormValid] = useState(false);
  const [, setObjectID] = useState<string | null>(null);
  const [credentialSets, setCredentialSets] = useState<CredentialSet[]>([]);
  const [deletedCredentialSets, setDeletedCredentialSets] = useState<
    CredentialSet[]
  >([]);
  const [localCredentialView, setLocalCredentialView] = useState('details');
  const [editingCredentialIndex, setEditingCredentialIndex] = useState<
    number | null
  >(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const requiredInputs = useMemo(() => {
    if (Array.isArray(providerData?.required_inputs)) {
      return providerData.required_inputs;
    }
    return ['Client ID', 'Client Secret'];
  }, [providerData]);

  const instructions = Array.isArray(providerData?.credential_description)
    ? providerData.credential_description
    : [];

  const credentialView = drawerCredentialView || localCredentialView;

  const updateCredentialView = useCallback(
    (view: 'details' | 'credential') => {
      setLocalCredentialView(view);
      setDrawerCredentialView?.(view);
    },
    [setDrawerCredentialView]
  );

  useEffect(() => {
    if (!providerData) return;

    form.setFieldsValue({
      name: providerData.sensor_name || providerData.name || '',
      description: getSensorDescriptionValue(providerData),
    });
    setObjectID(providerData.id ? providerData.id : null);
    setCredentialSets(getInitialCredentialSets(providerData, credentials));
    setDeletedCredentialSets([]);
    updateCredentialView('details');
    setEditingCredentialIndex(null);
  }, [credentials, form, providerData, updateCredentialView]);

  useEffect(() => {
    if (drawerCredentialView !== 'details') return;

    setEditingCredentialIndex(null);
    credentialForm.resetFields();
    credentialForm.setFieldsValue(
      buildBlankCredentialValues(requiredInputs, providerData?.provider_key)
    );
  }, [
    credentialForm,
    drawerCredentialView,
    requiredInputs,
    providerData?.provider_key,
  ]);

  const unauthorize = (error: ApiError) => {
    if (error?.status === 401) {
      removeToken();
      const _ = navigate('/login');
    }
  };

  const handleMenuClick = (key: string) => {
    setModalState((prev) => ({ ...prev, [key]: true }));
  };

  const openAddCredential = () => {
    setEditingCredentialIndex(null);
    credentialForm.resetFields();
    credentialForm.setFieldsValue(
      buildBlankCredentialValues(requiredInputs, providerData?.provider_key)
    );
    updateCredentialView('credential');
  };

  const openEditCredential = (index: number) => {
    const selected = credentialSets[index];
    setEditingCredentialIndex(index);
    credentialForm.setFieldsValue({
      name: selected.name,
      description: selected.description,
      ...selected.credential,
    });
    updateCredentialView('credential');
  };

  const handleCredentialDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;

    setCredentialSets((prev) => {
      const ids = prev.map((item) => item.local_key);
      const oldIndex = ids.indexOf(String(active.id));
      const newIndex = ids.indexOf(String(over.id));
      if (oldIndex === -1 || newIndex === -1) return prev;

      return markOrderUpdated(arrayMove(prev, oldIndex, newIndex));
    });
  };

  const deleteCredential = (index: number) => {
    setCredentialSets((prev) => {
      const removed = prev[index];
      if (removed?.id) {
        setDeletedCredentialSets((deleted) => [
          ...deleted,
          {
            ...removed,
            is_update: false,
            is_delete: true,
          },
        ]);
      }

      const next = prev.filter((_, itemIndex) => itemIndex !== index);
      return markOrderUpdated(next);
    });
  };

  const cancelCredentialEdit = () => {
    updateCredentialView('details');
    setEditingCredentialIndex(null);
    credentialForm.resetFields();
    credentialForm.setFieldsValue(
      buildBlankCredentialValues(requiredInputs, providerData?.provider_key)
    );
  };

  const onFinishCredential = async (values: CredentialFormValues) => {
    const credential = requiredInputs.reduce<Record<string, string>>(
      (acc, inputName) => {
        acc[inputName] = values[inputName];
        return acc;
      },
      {}
    );
    const sensorId = providerData?.sensor_id
      ? providerData?.sensor_id
      : providerData?.id;
    const verifyPayload = {
      is_primary:
        editingCredentialIndex === null
          ? credentialSets.length === 0
          : editingCredentialIndex === 0,
      description: values.description,
      credentials: credential,
      sensor_id: sensorId,
    };

    showSpin();
    try {
      const res = await VerifyCompanySensor(token, verifyPayload);
      const data = res?.data || null;
      if (!data?.data?.is_success) {
        message.error(data?.data?.message || 'Failed to verify credentials.');
        return;
      }
      if (data?.data?.message) {
        message.success(data.data.message);
      }
    } catch (err) {
      unauthorize(err as ApiError);
      return;
    } finally {
      hideSpin();
    }

    const nextCredential = {
      local_key:
        editingCredentialIndex === null
          ? getCredentialLocalKey()
          : credentialSets[editingCredentialIndex]?.local_key,
      name: values.name,
      description: values.description,
      is_primary:
        editingCredentialIndex === null
          ? credentialSets.length === 0
          : credentialSets[editingCredentialIndex]?.is_primary,
      is_update:
        editingCredentialIndex !== null &&
        Boolean(credentialSets[editingCredentialIndex]?.id),
      is_delete: false,
      credential,
    };

    setCredentialSets((prev) => {
      const next =
        editingCredentialIndex === null
          ? [...prev, nextCredential]
          : prev.map((item, index) =>
              index === editingCredentialIndex
                ? { ...item, ...nextCredential, id: item.id }
                : item
            );

      return syncPrimaryByOrder(next);
    });
    updateCredentialView('details');
    setEditingCredentialIndex(null);
    credentialForm.resetFields();
  };

  const linkSensor = (model: LinkSensorPayload) => {
    setIsFormValid(true);
    showSpin();
    LinkSensorsAccountBulk(token, model)
      .then((res) => {
        const data = res?.data || null;
        if (!data) return;

        if (data?.data?.is_success) {
          message.success(data?.data.message);
          onClose();
        } else {
          message.error(data?.data.message);
        }
      })
      .catch((err) => {
        unauthorize(err);
      })
      .finally(() => {
        hideSpin();
        setIsFormValid(false);
      });
  };

  const onFinish = (values: { description?: string }) => {
    if (credentialSets.length === 0) {
      message.error('Please add at least one credential set.');
      return;
    }

    const sensorId = providerData?.sensor_id
      ? providerData?.sensor_id
      : providerData?.id;

    const credentialPayload = credentialSets
      .map((item, index) => ({
        ...(item.id ? { id: item.id } : {}),
        is_primary: index === 0,
        description: item.description,
        credential_name: item.name,
        credentials: item.credential,
        sensor_id: sensorId,
        order_index: index,
        is_update: Boolean(item.is_update),
        is_delete: false,
        is_create: !item.id,
      }))
      .concat(
        deletedCredentialSets.map((item, index) => ({
          ...(item.id ? { id: item.id } : {}),
          is_primary: false,
          description: item.description,
          credential_name: item.name,
          credentials: item.credential,
          sensor_id: sensorId,
          order_index: credentialSets.length + index,
          is_update: false,
          is_delete: true,
          is_create: false,
        }))
      );

    linkSensor({
      sensor_id: sensorId,
      sensor_description: values.description || '',
      credentials: credentialPayload,
    });
  };

  return {
    cancelCredentialEdit,
    credentialForm,
    credentialSets,
    credentialView,
    deleteCredential,
    deleteModalVisible,
    editingCredentialIndex,
    form,
    handleCredentialDragEnd,
    handleMenuClick,
    instructions,
    notification,
    onFinish,
    onFinishCredential,
    openAddCredential,
    openEditCredential,
    requiredInputs,
    sensors,
    setDeleteModalVisible,
  };
};
